// HelpBar — the ⌘K command palette. One entry point that lets you navigate
// the app ("Go to" any page you can reach) and search help, plus a delegated
// "Find pallet / bin…" that opens the existing pallet-search modal. A pallet or
// bin number typed or scanned into the box puts "Find pallet or bin “…”" first,
// and it opens that modal with the number already in it (F09 UF-01).
//
// Mount <HelpBarRoot/> once (App.tsx). It owns ⌘K / Ctrl-K globally;
// <HelpBarButton/> (Dashboard header + every PageHeader) and a kiosk tap open
// the same palette. Nav actions come free from the module registry, filtered
// to what the user can actually reach (navModulesFor → hasModule + tenant-kind
// gate). Help results
// come from the in-browser Orama index (lazy-loaded). cmdk gives us the
// accessible combobox/listbox semantics and keyboard nav for free.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { confirmLeaveTo, wouldLeave } from '@/lib/pageDirty'
import { Command } from 'cmdk'
import { Search, CornerDownLeft, HelpCircle, Package, ArrowRight, Bug, Sparkles, Mic, GraduationCap, Headset } from 'lucide-react'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { usePermissions } from '@/lib/usePermissions'
import { usePersistedState } from '@/hooks/usePersistedState'
import { navModulesFor } from '@/lib/modules'
import { modulelessPagesFor, rankNavEntries, type NavEntry } from '@/lib/modulelessPages'
import { searchHelp, warmHelpSearch, type HelpSearchResult } from '@/lib/helpSearch'
import { helpTopicScope } from '@/lib/helpTopics'
import { offersTraining } from '@/lib/trainingGuides'
import { logHelpEvent } from '@/lib/helpEvents'
import { openPalletSearch } from '@/components/GlobalPalletSearch'
import { looksLikePalletOrBinNumber } from '@/lib/coldroomFind'
import { openBugReport } from '@/components/BugReportForm'
import { openSupportCall } from '@/components/SupportCall'
import { HelpDrawer } from '@/components/HelpButton'
import { AskBigAgriView } from '@/components/AskBigAgri'
import { prewarmAsk } from '@/lib/useAskStream'
import { shouldStageTranscript } from '@/lib/askTranscript'
import { useHasAddon } from '@/lib/useHasAddon'
import { useAskAccess } from '@/lib/askAccess'
import { usePushToTalk, primeSpeechOutput } from '@/lib/speech'
import { cloudTtsPrimed, prewarmCloudTts, primeCloudAudio } from '@/lib/cloudTts'
import { HELPBAR_OPEN_EVENT } from '@/components/HelpBarButton'
import { useSupportView } from '@/lib/platformView'

// Unlock BOTH ask output paths inside the triggering gesture. Cloud is primed
// once — re-priming resets the shared element's src, which would cut off a
// clip the cloud engine is still playing.
function primeAskAudio(): void {
  primeSpeechOutput()
  if (!cloudTtsPrimed()) primeCloudAudio()
  prewarmCloudTts() // gate chain + isolate warm while the answer is composing
}

// The trigger + open event live in HelpBarButton.tsx so a page header can
// carry the button without importing this module. Re-exported here because
// this file is where the palette's public API has always been documented.
export { openHelpBar } from '@/components/HelpBarButton'

// ---------------------------------------------------------------------------
// Root — mount once. Owns ⌘K + HELPBAR_OPEN_EVENT; renders the palette.
// ---------------------------------------------------------------------------
export function HelpBarRoot() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isMac = navigator.platform.toLowerCase().includes('mac')
      const mod   = isMac ? e.metaKey : e.ctrlKey
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen(o => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    function onOpen() { setOpen(true) }
    window.addEventListener(HELPBAR_OPEN_EVENT, onOpen)
    return () => window.removeEventListener(HELPBAR_OPEN_EVENT, onOpen)
  }, [])

  if (!open) return null
  return <HelpPalette onClose={() => setOpen(false)} />
}

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------
// fallow-ignore-next-line complexity
function HelpPalette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [drawerTopic, setDrawerTopic] = useState<string | null>(null)

  const { data: me } = useCurrentUser()
  const { data: tenant } = useActiveTenant()
  const [appMode] = usePersistedState<'packhouse' | 'farm'>('kf49.appMode', 'packhouse')

  useEffect(() => { inputRef.current?.focus(); warmHelpSearch() }, [])
  useEscapeKey(onClose)

  const q = query.trim().toLowerCase()
  // A /farm/ page IS the farm side, whatever Home's view toggle last said —
  // the rule the rail already follows (railModeFor: a gFarm route forces
  // 'farm'). Without it a farm user who reached a farm page without passing
  // Home first (a deep link, a fresh device) got the packhouse view's pages
  // under "Go to" — now one tap from every header (W-14).
  const navMode = pathname.startsWith('/farm/') ? 'farm' : appMode
  const { entries: navEntries, helpScope } = useNavMatches(me, tenant, navMode, q)
  const helpHits = useDebouncedHelpSearch(query, q, helpScope)

  // Telemetry: log the settled query (search / zero_result) — see the hook.
  const hitsRef = useRef(helpHits); hitsRef.current = helpHits
  useSettledSearchLog(q, query, pathname, hitsRef)

  // ⌘K is a navigation surface like any other, so it honours the unsaved-work
  // guard (src/lib/pageDirty.ts), with the rule a guarded link uses: Go to the
  // page you are on leaves nothing, so it does not ask (mid-walk, "Stop the
  // walk?" there stopped nothing — the page never unmounts). Close the palette
  // FIRST either way: leaving it mounted under the confirm would stack two
  // overlays, and if the operator chooses Keep editing they want the form back,
  // not the palette.
  const goto = (path: string, state?: { returnTo: string }) => {
    onClose()
    const here = pathname + search
    // Only a stamped link passes options, so every other Go to stays a bare navigate(path).
    const go = (to: string) => (state ? navigate(to, { state }) : navigate(to))
    if (!wouldLeave(path, here)) { go(path); return }
    void confirmLeaveTo(path, here).then(ok => { if (ok) go(path) })
  }
  const openTopic = (id: string) => setDrawerTopic(id)            // drawer over palette
  const findPallet = () => { onClose(); openPalletSearch() }
  // A pallet or bin number typed or scanned here goes to the finder WITH the
  // number (F09 UF-01) — it used to be dropped, and a scan's Enter opened Ask
  // BigAgri or Training. Anything with a space or a word is not a number.
  const findNumber = looksLikePalletOrBinNumber(query)
    ? () => { onClose(); openPalletSearch(query.trim()) }
    : null
  const scan = useScanEnter(findNumber, setQuery)
  // Close the palette first — BugReportRoot's modal is a sibling overlay, and
  // leaving the palette mounted would stack two z-50 dialogs.
  const reportProblem = () => { onClose(); openBugReport() }
  // Not in support view: a support call is about your OWN business, and the
  // server refuses one there anyway.
  const inSupportView = useSupportView()
  const talkToSupport = inSupportView ? null : () => { onClose(); openSupportCall() }

  // Ask BigAgri (add-on gated): selecting the Ask action swaps the palette for
  // the streamed-answer view; Back returns to the palette with the query intact.
  // A spoken question (P1: hold the mic, release to ask) auto-speaks its answer.
  // Who: packhouse members, and on a farm managers and admins only — the rule
  // the gateway enforces (src/lib/askAccess.ts); the entries hide for the rest.
  const askAccess = useAskAccess()
  const hasAsk = useHasAddon('ask_bigagri') && askAccess.allowed
  // Warm the gateway while the user is still typing — every telemetry
  // 'stall' error was the FIRST ask after an idle gap (cold isolate + caches).
  useEffect(() => { if (hasAsk) prewarmAsk() }, [hasAsk])
  const [askQuestion, setAskQuestion] = useState<string | null>(null)
  const [askVoice, setAskVoice] = useState(false)
  const ask = (question: string, viaVoice = false) => {
    primeAskAudio()  // inside the triggering gesture — unlocks iOS audio
    logHelpEvent({ eventType: 'ask_asked', route: pathname + (viaVoice ? '#voice' : ''), queryText: question })
    setAskVoice(viaVoice)
    setAskQuestion(question)
  }
  const ptt = usePushToTalk((transcript, confidence) => {
    setQuery(transcript)
    // Fragments and very-low-confidence transcripts stay staged in the input
    // — the Ask action is right there to confirm — instead of auto-firing.
    // Under-3-char transcripts also stay put (the Ask action needs ≥3).
    if (transcript.trim().length < 3 || shouldStageTranscript(transcript, confidence)) return
    ask(transcript, true)
  })
  // Mirror the live transcript into the input only once it EXISTS — starting
  // the mic must not wipe a query the user already typed.
  useEffect(() => { if (ptt.listening && ptt.interim) setQuery(ptt.interim) }, [ptt.listening, ptt.interim])

  if (askQuestion) {
    return (
      <AskBigAgriView
        question={askQuestion} route={pathname} autoSpeak={askVoice}
        onBack={() => setAskQuestion(null)} onClose={onClose}
        onNavigate={goto} />
    )
  }

  return (
    <>
      <PaletteDialog
        query={query} scan={scan} inputRef={inputRef} onClose={onClose}
        navEntries={navEntries} helpHits={helpHits}
        onGo={goto} onOpenTopic={openTopic} onFindPallet={findPallet}
        onReportProblem={reportProblem} onTalkToSupport={talkToSupport}
        onAsk={hasAsk && query.trim().length >= 3 ? ask : null}
        mic={hasAsk && ptt.supported ? ptt : null}
        onGoAskFloor={hasAsk ? () => goto('/ask', { returnTo: pathname + search }) : null}
        onGoTraining={offersTraining(tenant?.kind) ? () => goto('/training') : null} />
      {drawerTopic && <HelpDrawer topicId={drawerTopic} onClose={() => setDrawerTopic(null)} />}
    </>
  )
}

// Presentational shell — all markup, no state. Keeps HelpPalette to logic.
function PaletteDialog({ query, scan, inputRef, onClose, navEntries, helpHits, onGo, onOpenTopic, onFindPallet, onReportProblem, onTalkToSupport, onAsk, mic, onGoAskFloor, onGoTraining }: {
  query:         string
  scan:          ScanEnter
  inputRef:      React.RefObject<HTMLInputElement>
  onClose:       () => void
  navEntries:    NavEntry[]
  helpHits:      HelpSearchResult[]
  onGo:          (path: string) => void
  onOpenTopic:   (id: string) => void
  onFindPallet:  () => void
  onReportProblem: () => void
  onTalkToSupport: (() => void) | null
  onAsk:         ((question: string) => void) | null
  mic:           { listening: boolean; start: () => void; stop: () => void; abort: () => void } | null
  onGoAskFloor:  (() => void) | null
  onGoTraining:  (() => void) | null
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={onClose}
      data-theme="dark"
      className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 pt-[12vh]">
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-xl surface-sunk border hairline-strong rounded-2xl shadow-2xl overflow-hidden">
        <Command shouldFilter={false} loop label="Search and go to anywhere"
          onKeyDown={scan.onKeyDown}>
          <div className="flex items-center gap-2 border-b hairline px-3">
            <Search size={16} className="text-fg-muted shrink-0" />
            <Command.Input
              ref={inputRef}
              value={query}
              onValueChange={scan.onValueChange}
              placeholder={mic ? 'Search, or hold the mic to ask…' : 'Go to a page, or search help…'}
              className="flex-1 bg-transparent py-3 text-sm text-white placeholder-gray-500 outline-none"
            />
            {mic && <HoldToAskMic mic={mic} />}
          </div>
          <Command.List className="max-h-[60dvh] overflow-y-auto p-1.5">
            <Command.Empty className="px-3 py-6 text-center text-sm text-fg-muted">
              Nothing matches “{query}”.
            </Command.Empty>
            {/* First row, so it is the one picked: a scanner's Enter lands here. */}
            <FindNumberItem query={query} onFind={scan.findNumber} />
            <NavGroup entries={navEntries} onGo={onGo} />
            <HelpGroup hits={helpHits} onOpen={onOpenTopic} />
            <ActionsGroup query={query} onAsk={onAsk} onGoAskFloor={onGoAskFloor} onGoTraining={onGoTraining}
              onFindPallet={onFindPallet} onReportProblem={onReportProblem} onTalkToSupport={onTalkToSupport} />
          </Command.List>
        </Command>
      </div>
    </div>
  )
}

// The palette's Actions rows. Its own component so PaletteDialog stays markup
// with no branches of its own; each optional row is hidden by passing null.
function ActionsGroup({ query, onAsk, onGoAskFloor, onGoTraining, onFindPallet, onReportProblem, onTalkToSupport }: {
  query:           string
  onAsk:           ((question: string) => void) | null
  onGoAskFloor:    (() => void) | null
  onGoTraining:    (() => void) | null
  onFindPallet:    () => void
  onReportProblem: () => void
  onTalkToSupport: (() => void) | null
}) {
  return (
    <PaletteGroup heading="Actions">
      {onAsk && (
        <PaletteItem value={`ask bigagri question answer ${query}`}
          onSelect={() => onAsk(query.trim())}
          icon={<Sparkles size={15} className="text-violet-400" />}
          title={`Ask BigAgri: “${query.trim()}”`}
          sub="Answer from your live data, with the numbers and the page to prove them" />
      )}
      {onGoAskFloor && (
        <PaletteItem value="ask bigagri voice talk floor mode assistant"
          onSelect={onGoAskFloor}
          icon={<Mic size={15} className="text-violet-400" />}
          title="Ask BigAgri voice mode"
          sub="Full-screen hold-to-talk — made for the floor" />
      )}
      <TrainingItem onGo={onGoTraining} />
      <PaletteItem value="find pallet bin" onSelect={onFindPallet}
        icon={<Package size={15} className="text-amber-400" />}
        title="Find pallet / bin…" sub="Search pallets and bins by number or SSCC" />
      {/* The ONLY globally-reachable way to report a problem. <HelpButton/>
          carries the other entry point but sits on 5 of 228 routes, which
          is why support_tickets had zero rows. Keyword-rich `value` so
          "bug", "broken", "problem", "help" all surface it. */}
      <PaletteItem value="report problem bug broken issue support ticket feedback"
        onSelect={onReportProblem}
        icon={<Bug size={15} className="text-red-400" />}
        title="Report an app problem…" sub="Something broken or confusing? Tell us — takes 20 seconds" />
      {onTalkToSupport && (
        <PaletteItem value="talk to support call voice help show me where human assistant"
          onSelect={onTalkToSupport}
          icon={<Headset size={15} className="text-emerald-400" />}
          title="Talk to support…" sub="Ask out loud — she can show you on screen where to tap" />
      )}
    </PaletteGroup>
  )
}

type PaletteMic = { listening: boolean; start: () => void; stop: () => void; abort: () => void }

function HoldToAskMic({ mic }: { mic: PaletteMic }) {
  return (
    <button
      type="button"
      // Pointer capture keeps the hold alive through finger drift; a
      // CANCELLED gesture aborts (must never submit); release submits.
      onPointerDown={(e) => {
        e.preventDefault()
        try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* pointer already gone */ }
        mic.start()
        primeAskAudio()
      }}
      onPointerUp={mic.stop}
      onPointerCancel={mic.abort}
      onContextMenu={(e) => e.preventDefault()}
      style={{ touchAction: 'none' }}
      aria-label="Hold to ask by voice"
      title="Hold to ask Ask BigAgri by voice"
      className={`shrink-0 rounded-lg p-2 min-h-[44px] min-w-[44px] flex items-center justify-center select-none ${mic.listening ? 'bg-violet-600 text-white animate-pulse' : 'text-fg-muted hover:text-fg-secondary'}`}>
      <Mic size={16} />
    </button>
  )
}

// The nav items the user can reach (registry + permission/kind gate), ranked
// by the query, and the help-topic scope for this business's kind and view
// (OR-07: the Help group offers this kind's articles to everyone in it, never
// only the person's own pages). Null scope while the tenant is still loading,
// so hits are left unscoped rather than emptied.
function useNavMatches(
  me: ReturnType<typeof useCurrentUser>['data'],
  tenant: ReturnType<typeof useActiveTenant>['data'],
  appMode: 'packhouse' | 'farm',
  q: string,
): { entries: NavEntry[]; helpScope: ((topicId: string) => boolean) | null } {
  const { perms } = usePermissions()
  const navItems = useMemo(
    () => navModulesFor(me ?? null, { kind: tenant?.kind, enabledModules: tenant?.enabledModules, appMode, permMap: perms, slug: tenant?.slug }),
    [me, perms, tenant?.kind, tenant?.enabledModules, tenant?.slug, appMode],
  )
  const helpScope = useMemo(
    () => (tenant?.kind ? helpTopicScope(tenant.kind, appMode, tenant.slug) : null),
    [tenant?.kind, tenant?.slug, appMode],
  )
  return useMemo(() => ({
    entries: rankNavEntries(navItems, modulelessPagesFor(navItems), q),
    helpScope,
  }), [navItems, q, helpScope])
}

// A wedge scanner types the number and presses Enter within a few
// milliseconds. The number row renders first, so it is picked — but cmdk moves
// its pick in a later effect, and an Enter that beats it would open whatever
// was picked before (Training, Ask BigAgri). So Enter on a number goes to the
// finder outright, unless the operator has moved the pick with the arrow keys
// since they last typed: then Enter is theirs, and cmdk opens what they chose.
function useScanEnter(onFindNumber: (() => void) | null, onQueryChange: (v: string) => void) {
  const moved = useRef(false)
  return {
    findNumber: onFindNumber,
    onValueChange: (v: string) => { moved.current = false; onQueryChange(v) },
    onKeyDown: (e: React.KeyboardEvent) => {
      const ctrlMove = e.ctrlKey && ['n', 'j', 'p', 'k'].includes(e.key)   // cmdk's vim bindings
      if (ctrlMove || ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) { moved.current = true; return }
      if (e.key !== 'Enter' || !onFindNumber || moved.current) return
      if (e.nativeEvent.isComposing) return
      e.preventDefault()
      onFindNumber()
    },
  }
}

type ScanEnter = ReturnType<typeof useScanEnter>

// The "Find pallet or bin “…”" row — only while the box holds a number.
function FindNumberItem({ query, onFind }: { query: string; onFind: (() => void) | null }) {
  if (!onFind) return null
  return (
    <PaletteItem value={`find number ${query.trim()}`} onSelect={onFind}
      icon={<Package size={15} className="text-amber-400" />}
      title={`Find pallet or bin “${query.trim()}”`}
      sub="Open the pallet finder with this number" />
  )
}

function useEscapeKey(onClose: () => void) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
}

// Debounced help search (200ms) over the lazy Orama index. `q` is the trimmed,
// lower-cased query used as the min-length gate; `query` is the raw term. With
// a scope it over-fetches, drops topics outside this kind's catalogue, then
// keeps the top HELP_HITS — so scoping never shortens the list needlessly.
const HELP_HITS = 6
function useDebouncedHelpSearch(
  query: string, q: string, scope: ((topicId: string) => boolean) | null,
): HelpSearchResult[] {
  const [hits, setHits] = useState<HelpSearchResult[]>([])
  useEffect(() => {
    if (q.length < 2) { setHits([]); return }
    let alive = true
    const t = setTimeout(() => {
      searchHelp(query, scope ? HELP_HITS * 4 : HELP_HITS).then(h => {
        if (alive) setHits(scope ? h.filter(x => scope(x.id)).slice(0, HELP_HITS) : h)
      })
    }, 200)
    return () => { alive = false; clearTimeout(t) }
  }, [query, q, scope])
  return hits
}

// Log the SETTLED help query once (1.2s after the last keystroke) as search or
// zero_result — the zero-results are the authoring backlog. hitsRef reads the
// latest results (the 200ms search resolves well inside the settle window); a
// per-string guard stops a re-settle from double-logging.
function useSettledSearchLog(
  q: string, query: string, route: string,
  hitsRef: React.MutableRefObject<HelpSearchResult[]>,
) {
  const lastLogged = useRef('')
  useEffect(() => {
    if (q.length < 2) return
    const t = setTimeout(() => {
      if (lastLogged.current === q) return
      lastLogged.current = q
      logHelpEvent({ eventType: hitsRef.current.length > 0 ? 'search' : 'zero_result', route, queryText: query })
    }, 1200)
    return () => clearTimeout(t)
  }, [q, query, route, hitsRef])
}

const GROUP_HEADING_CLS =
  '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-fg-muted'

function PaletteGroup({ heading, children }: { heading: string; children: React.ReactNode }) {
  return <Command.Group heading={heading} className={GROUP_HEADING_CLS}>{children}</Command.Group>
}

function NavGroup({ entries, onGo }: { entries: NavEntry[]; onGo: (path: string) => void }) {
  if (!entries.length) return null
  return (
    <PaletteGroup heading="Go to">
      {entries.map(e => e.module ? (
        <PaletteItem key={`nav-${e.module.id}`} value={`nav ${e.module.label} ${e.module.id} ${e.module.key}`}
          onSelect={() => onGo(e.module.path)}
          icon={<ArrowRight size={15} className="text-blue-400" />}
          title={e.module.label} sub={e.module.description} />
      ) : (
        <PaletteItem key={`page-${e.page.id}`} value={`nav ${e.page.label} ${e.page.id} ${e.page.keywords}`}
          onSelect={() => onGo(e.page.path)}
          icon={<ArrowRight size={15} className="text-blue-400" />}
          title={e.page.label} sub={e.page.description} />
      ))}
    </PaletteGroup>
  )
}

function HelpGroup({ hits, onOpen }: { hits: HelpSearchResult[]; onOpen: (id: string) => void }) {
  if (!hits.length) return null
  return (
    <PaletteGroup heading="Help">
      {hits.map(h => (
        <PaletteItem key={`help-${h.id}`} value={`help ${h.id} ${h.title}`}
          onSelect={() => onOpen(h.id)}
          icon={<HelpCircle size={15} className="text-emerald-400" />}
          title={h.title} sub={h.snippet} />
      ))}
    </PaletteGroup>
  )
}

// Its own component so the palette body carries no extra branch for it.
function TrainingItem({ onGo }: { onGo: (() => void) | null }) {
  if (!onGo) return null
  return (
    <PaletteItem value="training guide guides video videos manual learn how to course"
      onSelect={onGo}
      icon={<GraduationCap size={15} className="text-sky-400" />}
      title="Training" sub="Every page's guide and video in one place" />
  )
}

function PaletteItem({ value, onSelect, icon, title, sub }: {
  value:    string
  onSelect: () => void
  icon:     React.ReactNode
  title:    string
  sub?:     string
}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="group flex items-center gap-2.5 rounded-lg px-2 py-2 cursor-pointer data-[selected=true]:bg-surface text-fg-secondary">
      <span className="shrink-0">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm text-white truncate">{title}</span>
        {sub && <span className="block text-[11px] text-fg-muted truncate">{sub}</span>}
      </span>
      <CornerDownLeft size={13} className="shrink-0 text-fg-muted opacity-0 group-data-[selected=true]:opacity-100" />
    </Command.Item>
  )
}
